import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { BoxPage, FilterButton } from '@/components/common'
import { Card } from '@/@core/components/mui/Card'
import {
  CardContent,
  Divider,
  Grid,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Chip,
  Button,
  MenuItem,
  Checkbox,
  Menu
} from '@mui/material'
import { useNotification, useRouteQuery, useTranslator } from '@/hooks'
import { ActivityLogTypeEnum, Attendances, User } from '@/@data'
import { Transaction, TransactionDetailsStatusEnum } from '@/@data/transactions'
import { TabButton } from '@/components/common/button/TabButton'
import FilterMonthTransaction from './FilterMonthTransaction'
import { TableLimit } from '@/@core/components/mui/TableLimit'
import { TransactionDetailTableEmployer } from './TransactionDetailTableEmployer'
import dayjs from 'dayjs'
import TransactionDetailTableTransaction from './TransactionDetailTableTransaction'
import CustomAvatar from '@/@core/components/mui/Avatar'
import Link from '@/components/Link'
import { getInitials } from '@/utils/getInitials'
import TransactionStatusSelection from '../selection/TransactionStatusSelection'
import { SearchInput } from '../common/SearchInput'
import InputDateRangerField from '../common/input/InputDateRangerField'
import AppLogs from '../common/AppLogs'
import UserStatusSelection from '../selection/UserStatusSelection'
import isBetween from 'dayjs/plugin/isBetween'

dayjs.extend(isBetween)

const TransactionDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { __, __cc } = useTranslator()
  const n = useNotification()
  const [tabValue, setTabValue] = useState(0)
  const { searchQuery, updateQuery } = useRouteQuery()
  const [filter, setFilter] = useState(true)
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null)

  // Column visibility state for transaction detail table
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    employees: true,
    request_transaction: true,
    create_date: true,
    update_date: true,
    status: true,
    amount: true,
    eligibility_rate: true,
    eligibility_amount: true,
    service_fee: true,
    voucher_discount: true
  })

  // Columns configuration
  const columns = [
    { key: 'employees', label: __('Employees') },
    { key: 'request_transaction', label: __('Request Transaction') },
    { key: 'create_date', label: __('Create Date Transaction') },
    { key: 'update_date', label: __('Update Date') },
    { key: 'status', label: __('Status Transaction') },
    { key: 'amount', label: __('Amount EWA Disbursement') },
    { key: 'eligibility_rate', label: __('Eligibility rate') },
    { key: 'eligibility_amount', label: __('Est. Max EWA Disbursement') },
    { key: 'service_fee', label: __('Services Fee') },
    { key: 'voucher_discount', label: __('Voucher') }
  ]

  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length

  // Only count toggleable columns (exclude first and last)
  const toggleableColumns = columns.slice(1, -1)
  const visibleToggleableCount = toggleableColumns.filter(col => columnVisibility[col.key] !== false).length

  const isAllSelected = visibleToggleableCount === toggleableColumns.length
  const isIndeterminate = visibleToggleableCount > 0 && visibleToggleableCount < toggleableColumns.length

  // Column selection handlers
  const handleSelectAll = (checked: boolean) => {
    const newVisibility = { ...columnVisibility }

    // Only toggle the middle columns (exclude first and last)
    toggleableColumns.forEach(col => {
      newVisibility[col.key] = checked
    })

    setColumnVisibility(newVisibility)
  }

  const handleColumnToggle = (columnKey: string) => {
    const columnIndex = columns.findIndex(col => col.key === columnKey)

    // Don't allow toggling first and last columns
    if (columnIndex === 0 || columnIndex === columns.length - 1) {
      return
    }

    const newVisibility = {
      ...columnVisibility,
      [columnKey]: !columnVisibility[columnKey]
    }

    setColumnVisibility(newVisibility)
  }

  const companyId = Number(id)
  const queryString = searchQuery.toString()

  const paramsCompany = useMemo(() => {
    return {
      companyId: companyId,
      month: searchQuery.get('month')
    }
  }, [searchQuery.get('month'), searchQuery.get('companyId')])

  const { data: dataAttendance, refetch: refetchCompany } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(paramsCompany),
    enabled: !!searchQuery.get('month') && tabValue === 0
  })

  const paramsCompanyBeforeMonth = useMemo(() => {
    return {
      companyId: companyId,
      month: dayjs(searchQuery.get('month')).subtract(1, 'month').format('YYYY-MM-DD')
    }
  }, [searchQuery.get('month'), searchQuery.get('companyId')])

  const { data: dataAttendanceBefore } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(paramsCompanyBeforeMonth),
    enabled: !!searchQuery.get('month') && tabValue === 0
  })

  const paramsEmployee = useMemo(() => {
    return {
      month: searchQuery.get('month'),
      page: searchQuery.get('page'),
      perPage: searchQuery.get('perPage'),
      user_keyword: searchQuery.get('user_keyword'),
      status: searchQuery.get('status'),
      created_from: searchQuery.get('created_from')
        ? searchQuery.get('created_from')
        : dataAttendance?.data?.attendance?.startDate,
      created_to: searchQuery.get('created_to')
        ? searchQuery.get('created_to')
        : dataAttendance?.data?.attendance?.endDate
    }
  }, [queryString, tabValue === 0])

  const { data: transactionEmployeeData } = useQuery({
    ...Transaction.getCompanyTransactionsEmployeeByCompanyId(companyId, paramsEmployee),
    enabled:
      !!id &&
      tabValue === 0 &&
      !!dataAttendance?.data?.attendance?.startDate &&
      !!dataAttendance?.data?.attendance?.endDate
  })

  //   // Employees list for the second tab (reuse EmployerDetail table)
  const params = useMemo(() => {
    return {
      company_id: companyId,
      month: searchQuery.get('month')
    }
  }, [companyId, searchQuery.get('month')])

  const { data: transactionData } = useQuery({
    ...Transaction.getCompanyTransactionsDetailByCompanyId(params),
    enabled: !!companyId && !!searchQuery.get('month')
  })

  const employeeParams = useMemo(() => {
    return {
      company_id: companyId,
      created_at: searchQuery.get('date'),
      page: searchQuery.get('page'),
      keywords: searchQuery.get('user_keyword'),
      eligibility_status: searchQuery.get('eligibility_status'),
      user_type: 'user',
      status: searchQuery.get('status'),
      perPage: searchQuery.get('perPage'),
      created_from: searchQuery.get('created_from'),
      created_to: searchQuery.get('created_to')
    }
  }, [searchQuery.toString(), tabValue === 1])

  // console.log(transactionData?.data?.contract)

  const { data: employeesData, refetch: employeesRefetch } = useQuery({
    ...User.objects.paginateQueryFactory(employeeParams),
    enabled: !!companyId && tabValue === 1
  })

  const { mutate: exportTransactionData, isPending: isExporting } = useMutation({
    mutationFn: (params: any) => Transaction.exportCompanyTransactionsEmployeeByCompanyId(companyId, params).queryFn()
  })

  const handleExportTransactionData = () => {
    exportTransactionData({
      ...employeeParams,
      is_export: true
    })
  }

  const calculateContractDays = () => {
    if (!transactionData?.data?.contract?.start_date || !transactionData?.data?.contract?.end_date) {
      return { totalDays: 0, remainingDays: 0, progressPercentage: 0 }
    }

    const startDate = dayjs(transactionData.data.contract.start_date)
    const endDate = dayjs(transactionData.data.contract.end_date)
    const now = dayjs()
    const totalDays = startDate && endDate ? endDate.diff(startDate, 'day') + 1 : 0
    const passedDays = startDate ? Math.min(now.diff(startDate, 'day'), totalDays) : 0
    const remainingDays = totalDays - passedDays
    const progressPercentage = totalDays > 0 ? Math.min(Math.max((passedDays / totalDays) * 100, 0), 100) : 0

    return { totalDays, remainingDays, progressPercentage }
  }

  const calculateTimeSinceUpdate = () => {
    if (!transactionData?.data?.last_updated_at) return null

    const updatedAt = dayjs(transactionData.data.last_updated_at)
    const now = dayjs()
    const diffInMinutes = now.diff(updatedAt, 'minute')
    const diffInHours = now.diff(updatedAt, 'hour')
    const diffInDays = now.diff(updatedAt, 'day')

    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${__('minutes ago')}`
    } else if (diffInHours < 24) {
      return `${diffInHours} ${__('hours ago')}`
    } else if (diffInDays < 7) {
      return `${diffInDays} ${__('day ago')}`
    } else {
      return `${diffInDays} ${__('day ago')}`
    }
  }

  const getAvatar = (params: { avatar_file: any; first_name: string }) => {
    const { avatar_file, first_name } = params

    if (avatar_file) {
      return <CustomAvatar src={avatar_file.tiny_url} skin='light' size={80} />
    } else {
      return (
        <CustomAvatar skin='light' size={80}>
          {getInitials(first_name as string)}
        </CustomAvatar>
      )
    }
  }

  useEffect(() => {
    // Only clear filters when switching to tab 1 (List Employee)
    if (tabValue === 1) {
      updateQuery({
        date: undefined,
        status: undefined,
        page: undefined,
        user_keyword: undefined,
        eligibility_status: undefined,
        perPage: undefined,
        created_from: undefined,
        created_to: undefined
      })
    }
  }, [tabValue])

  useEffect(() => {
    // When on tab 0, ensure created_from/to follow attendance window
    if (tabValue === 0 && dataAttendance?.data?.attendance?.startDate && dataAttendance?.data?.attendance?.endDate) {
      const startDate = dayjs(dataAttendance.data.attendance.startDate).format('YYYY-MM-DD')
      const endDate = dayjs(dataAttendance.data.attendance.endDate).format('YYYY-MM-DD')

      const currentFrom = searchQuery.get('created_from')
      const currentTo = searchQuery.get('created_to')

      // Only update when different to avoid loops
      if (currentFrom !== startDate || currentTo !== endDate) {
        updateQuery({ created_from: startDate, created_to: endDate })
      }
    }
  }, [dataAttendance, searchQuery.get('month'), tabValue])
  // Refetch when month or companyId changes
  useEffect(() => {
    if (companyId && searchQuery.get('month')) {
      refetchCompany()
    }
  }, [companyId, searchQuery.get('month')])

  const [isOpenLogs, setIsOpenLogs] = useState(false)

  return (
    <>
      <BoxPage
        showBackButton={true}
        title={__('Transaction Employer Detail')}
        breadcrumbs={[
          { label: __('Finance Management'), href: '/cms/finance/transaction' },
          { label: __('Transaction Management'), href: '/cms/finance/transaction' },
          { label: __('Transaction Employer Detail') }
        ]}
      >
        <div className='mb-6'>
          <FilterMonthTransaction
            className='rounded-[28px] px-6 py-3'
            onDateChange={month => updateQuery({ month })}
            dataBeforeMonth={dataAttendanceBefore?.data}
            isCurrentPayPeriod={dataAttendanceBefore?.data?.current_pay_period}
            selectedDate={
              tabValue === 0 && dataAttendance?.data?.attendance?.startDate
                ? dayjs(dataAttendance?.data?.attendance?.startDate).toDate()
                : undefined
            }
            startDate={
              tabValue === 0 && dataAttendance?.data?.attendance?.startDate
                ? dayjs(dataAttendance?.data?.attendance?.startDate).toDate()
                : undefined
            }
            endDate={
              tabValue === 0 && dataAttendance?.data?.attendance?.endDate
                ? dayjs(dataAttendance?.data?.attendance?.endDate).toDate()
                : undefined
            }
          />
        </div>

        <Card
          sx={{
            borderRadius: 3,
            mb: 6,
            backgroundColor:
              transactionData?.data?.status === 'expired'
                ? '#FFDBDC'
                : transactionData?.data?.status === 'active'
                  ? '#E9F9EF'
                  : transactionData?.data?.status === 'disable'
                    ? '#EAEAEC'
                    : '#FFFFFF'
          }}
        >
          <CardContent>
            <div className='flex justify-between mb-6'>
              <div className='flex gap-2'>
                <div className='flex items-center gap-4'>
                  <Link href={`/cms/employer/${companyId}`} className='flex items-center gap-4 no-underline'>
                    {getAvatar({
                      avatar_file: transactionData?.data?.logo_file,
                      first_name: transactionData?.data?.name || '-'
                    })}
                  </Link>
                  <div className='flex flex-col'>
                    <div className='flex items-center justify-between gap-4'>
                      <Tooltip title={transactionData?.data?.name || '-'} placement='top' arrow>
                        <Typography
                          variant='h3'
                          className='font-medium max-w-[700px] whitespace-nowrap overflow-hidden text-ellipsis'
                        >
                          {transactionData?.data?.name || '-'}
                        </Typography>
                      </Tooltip>
                    </div>

                    <div className='flex items-center gap-2 text-[#2F2B3D66] mt-2'>
                      <i className='tabler-id text-[18px]' />
                      <Typography variant='body2' color='text.secondary'>
                        {transactionData?.data?.company_code || '-'}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() => {
                          navigator.clipboard.writeText(transactionData?.data?.company_code || '')
                          n.success(__('Copied to clipboard!'))
                        }}
                      >
                        <i className='tabler-copy' />
                      </IconButton>
                    </div>
                  </div>
                </div>

                <div>
                  <Chip
                    label={transactionData?.data?.status as TransactionDetailsStatusEnum}
                    sx={{
                      textTransform: 'capitalize',
                      borderRadius: '999px',
                      padding: '12px 4px',
                      color: '#fff',
                      fontSize: '13px',
                      backgroundColor:
                        transactionData?.data?.status === 'active'
                          ? '#28C76F'
                          : transactionData?.data?.status === 'disable'
                            ? '#6B7280'
                            : transactionData?.data?.status === 'expired'
                              ? '#FF4C51'
                              : 'default'
                    }}
                  />
                </div>
              </div>

              <div className='flex gap-2'>
                <Tooltip title='History'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setIsOpenLogs(true)
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#2E263D14',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <i className='tabler-history' style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            {transactionData?.data?.status !== 'expired' && transactionData?.data?.status !== null && (
              <div className='flex-1 pb-4'>
                {(() => {
                  const { remainingDays } = calculateContractDays()

                  return (
                    <>
                      <Typography
                        variant='caption'
                        className={`mt-1 block ${
                          transactionData?.data?.status === 'Active'
                            ? 'text-black-600'
                            : transactionData?.data?.status === 'disable'
                              ? 'text-gray-500'
                              : 'text-gray-500'
                        }`}
                      >
                        {remainingDays === 0
                          ? __('0 days remaining until your Active Contract requires update')
                          : `${remainingDays} ${__('days remaining until your Active Contract expires')}`}
                      </Typography>
                    </>
                  )
                })()}
              </div>
            )}

            {transactionData?.data?.status === null && (
              <div className='flex-1 pb-4'>
                <Typography variant='caption' className='text-red-500'>
                  {__('Employer does not have a contract. Please upload the contract.')}
                </Typography>
              </div>
            )}

            {/* Active Contract Section */}

            {transactionData?.data?.status === 'expired' && (
              <div className='bg-[#fff] rounded-[12px] p-6 mb-6 border'>
                <Typography variant='h5' className='mb-3 text-[#000]'>
                  {__('Active Contract')}
                </Typography>

                <Divider />

                <div className='bg-[#2F2B3D0F] p-4 mt-4 flex items-center justify-between rounded-[12px]'>
                  <div className=''>
                    <div className='flex items-center gap-5 mb-2'>
                      <Typography variant='subtitle1' className='font-medium text-[#000] text-[18px]'>
                        {__('Active Contract')}
                      </Typography>
                      <Chip
                        label={transactionData?.data?.status as TransactionDetailsStatusEnum}
                        sx={{
                          textTransform: 'capitalize',
                          borderRadius: '999px',
                          padding: '0px 4px',
                          color: '#FF4C51',
                          fontWeight: '500',
                          fontSize: '13px',
                          backgroundColor: '#FF4C5129'
                        }}
                      />
                    </div>
                    <div className='flex items-center gap-2'>
                      <Box sx={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant='body2' className='text-[#000] w'>
                          {transactionData?.data?.contract?.file?.name || '-'}
                        </Typography>
                      </Box>

                      <IconButton
                        size='small'
                        sx={{ width: 24, height: 24 }}
                        onClick={() => window.open(transactionData?.data?.contract?.file?.large_url, '_blank')}
                      >
                        <i className='tabler-eye text-[#666]' style={{ fontSize: 16 }} />
                      </IconButton>
                    </div>
                    <div className='flex items-center gap-2 pt-2'>
                      <div className='flex items-center gap-1'>
                        <div>
                          {__('Start')}: {dayjs(transactionData?.data?.contract?.start_date).format('DD MMM YYYY')}
                        </div>
                        -
                        <div>
                          {__('End')}: {dayjs(transactionData?.data?.contract?.end_date).format('DD MMM YYYY')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='w-[50%]'>
                    <div className='flex items-center gap-3'>
                      <div className='flex-1'>
                        {(() => {
                          const { totalDays, remainingDays, progressPercentage } = calculateContractDays()

                          return (
                            <>
                              <div className='flex items-center justify-between mb-1'>
                                <Typography variant='body2' className='text-[#000]'>
                                  {__('Days')}
                                </Typography>
                                <Typography variant='body2' className='text-[#000] font-medium'>
                                  {totalDays - remainingDays} of {totalDays} {__('Days')}
                                </Typography>
                              </div>
                              <div className='w-full bg-gray-200 rounded-full h-2'>
                                <div
                                  className={`h-2 rounded-full ${
                                    progressPercentage < 70
                                      ? 'bg-blue-500'
                                      : progressPercentage < 100
                                        ? 'bg-orange-500'
                                        : 'bg-red-500'
                                  }`}
                                  style={{ width: `${progressPercentage}%` }}
                                ></div>
                              </div>
                              <Typography variant='caption' className='text-[#000] mt-1 block'>
                                {remainingDays === 0
                                  ? __('0 days remaining until your Active Contract requires update')
                                  : `${remainingDays} ${__('days remaining until your Active Contract expires')}`}
                              </Typography>
                            </>
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className='bg-[#fff] rounded-[12px] p-6'>
              <div className='flex items-center justify-between mb-4'>
                <Typography variant='h6' className='text-[18px] font-medium'>
                  {__('Statistics')}
                </Typography>
                {transactionData?.data?.last_updated_at && (
                  <Typography color='text.secondary' className='flex items-center gap-1 text-[13px] text-[#ccc]'>
                    {__('Updated')} {calculateTimeSinceUpdate()}
                  </Typography>
                )}
              </div>
              <Grid container spacing={3}>
                <Grid size={4}>
                  <div className='flex items-center gap-2'>
                    <div className='flex items-center gap-2 bg-[#FF9F4329] rounded-sm p-2'>
                      <i className='tabler-credit-card text-[#FF9F43]' />
                    </div>
                    <div>
                      <Typography variant='h6'>{__cc(transactionData?.data?.request_num)}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {__('Request Transaction')}
                      </Typography>
                    </div>
                  </div>
                </Grid>
                <Grid size={4}>
                  <div className='flex items-center gap-2'>
                    <div className='flex items-center gap-2 bg-[#28C76F29] rounded-sm p-2'>
                      <i className='tabler-currency-dollar text-[#28C76F]' />
                    </div>
                    <div>
                      <Typography variant='h6'>{__cc(transactionData?.data?.request_amount)}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {__('Total EWA Disbursement')}
                      </Typography>
                    </div>
                  </div>
                </Grid>
                <Grid size={4}>
                  <div className='flex items-center gap-2'>
                    <div className='flex items-center gap-2 bg-[#00BAD129] rounded-sm p-2'>
                      <i className='tabler-chart-pie-2 text-[#00BAD1]' />
                    </div>
                    <div>
                      <Typography variant='h6'>{__cc(transactionData?.data?.eligibility_amount)}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {__('Est. Max EWA Disbursement')}
                      </Typography>
                    </div>
                  </div>
                </Grid>
                <Grid size={4}>
                  <div className='flex items-center gap-2 pt-4'>
                    <div className='flex items-center gap-2 bg-[#3551F229] rounded-sm p-2'>
                      <i className='tabler-users text-[#3551F2]' />
                    </div>
                    <div>
                      <Typography variant='h6'>{__cc(transactionData?.data?.total_user)}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {__('Employees')}
                      </Typography>
                    </div>
                  </div>
                </Grid>
                <Grid size={4}>
                  <div className='flex items-center gap-2 pt-4'>
                    <div className='flex items-center gap-2 bg-[#6F83F629] rounded-sm p-2'>
                      <i className='tabler-wallet text-[#6F83F6]' />
                    </div>
                    <div>
                      <Typography variant='h6'>{__cc(transactionData?.data?.service_fee)}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {__('Services Fee')}
                      </Typography>
                    </div>
                  </div>
                </Grid>
                <Grid size={4}>
                  <div className='flex items-center gap-2 pt-4'>
                    <div className='flex items-center gap-2 bg-[#34627229] rounded-sm p-2'>
                      <i className='tabler-receipt-tax text-[#346272]' />
                    </div>
                    <div>
                      <Typography variant='h6'>{__cc(transactionData?.data?.voucher_discount)}</Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {__('Total Voucher')}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
          </CardContent>
        </Card>

        <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
          <TabButton
            label={__('Current Transactions')}
            icon={<i className='tabler-credit-card' />}
            isSelected={tabValue === 0}
            onClick={() => setTabValue(0)}
          />
          <TabButton
            label={__('List Employee')}
            icon={<i className='tabler-users' />}
            isSelected={tabValue === 1}
            onClick={() => setTabValue(1)}
          />
        </Box>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ padding: 0 }}>
            {tabValue === 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    padding: '20px',
                    gap: 4,
                    flexDirection: 'column'
                  }}
                >
                  {filter && (
                    <>
                      <Box className='mb-3'>
                        <Typography variant='h6' className='mb-3'>
                          {__('Filters')}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={6}>
                            <InputDateRangerField
                              value={
                                searchQuery.get('created_from') && searchQuery.get('created_to')
                                  ? [searchQuery.get('created_from') as string, searchQuery.get('created_to') as string]
                                  : undefined
                              }
                              onChange={created_at => {
                                if (created_at) {
                                  updateQuery({ created_from: created_at[0], created_to: created_at[1], page: 1 })
                                } else {
                                  updateQuery({ created_from: undefined, created_to: undefined, page: 1 })
                                }
                              }}
                            />
                          </Grid>
                          <Grid size={6}>
                            <TransactionStatusSelection
                              value={searchQuery.get('status') || ''}
                              onSelect={status => updateQuery({ status, page: 1 })}
                              placeholder={__('All')}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Divider />
                    </>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <TableLimit />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <SearchInput
                        placeholder={__('Search Employee')}
                        initialValue={searchQuery.get('user_keyword') || ''}
                        onSearch={value => {
                          const current = searchQuery.get('user_keyword') || ''

                          if (value !== current) {
                            updateQuery({ user_keyword: value, page: 1 })
                          }
                        }}
                      />

                      <FilterButton filter={filter} onFilter={() => setFilter(!filter)} />

                      <Button
                        variant='tonal'
                        color={'primary'}
                        size='small'
                        startIcon={<i className='tabler-file-export' />}
                        className='rounded-full'
                        onClick={() => {
                          handleExportTransactionData()
                        }}
                        disabled={isExporting}
                      >
                        {__('Export Transaction Data')}
                      </Button>

                      {/* Column Selection Button */}
                      <Button
                        variant='outlined'
                        onClick={e => setColumnsMenuAnchor(e.currentTarget)}
                        sx={{
                          borderColor: '#ccc',
                          color: '#000',
                          borderRadius: 999,
                          textTransform: 'capitalize',
                          '&:hover': {
                            borderColor: '#3551F2',
                            backgroundColor: '#3551F229'
                          }
                        }}
                        endIcon={<i className='tabler-chevron-down' style={{ fontSize: 16 }} />}
                      >
                        {__('Columns')} ({visibleColumnsCount})
                      </Button>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Column Selection Menu */}
                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={Boolean(columnsMenuAnchor)}
                  onClose={() => setColumnsMenuAnchor(null)}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        borderRadius: 2
                      }
                    }
                  }}
                >
                  <MenuItem>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {__('Select All')}
                    </Typography>
                  </MenuItem>

                  <Divider />
                  {columns.map((column, index) => {
                    const isFirstOrLast = index === 0 || index === columns.length - 1

                    return (
                      <MenuItem
                        key={column.key}
                        onClick={() => !isFirstOrLast && handleColumnToggle(column.key)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          opacity: isFirstOrLast ? 0.6 : 1,
                          cursor: isFirstOrLast ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Checkbox
                          checked={columnVisibility[column.key] || false}
                          onChange={() => !isFirstOrLast && handleColumnToggle(column.key)}
                          onClick={e => e.stopPropagation()}
                          disabled={isFirstOrLast}
                        />
                        <Typography variant='body2'>{column.label}</Typography>
                      </MenuItem>
                    )
                  })}
                </Menu>

                <TransactionDetailTableTransaction
                  data={transactionEmployeeData?.data || []}
                  meta={(transactionEmployeeData as any)?.meta}
                  isTransaction={true}
                  columnVisibility={columnVisibility}
                />
              </>
            )}

            {tabValue === 1 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    padding: '20px',
                    gap: 4,
                    flexDirection: 'column'
                  }}
                >
                  {filter && (
                    <>
                      <Box className='mb-3'>
                        <Typography variant='h6' className='mb-3'>
                          {__('Filters')}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={6}>
                            <InputDateRangerField
                              value={
                                searchQuery.get('created_from') && searchQuery.get('created_to')
                                  ? [searchQuery.get('created_from') as string, searchQuery.get('created_to') as string]
                                  : undefined
                              }
                              onChange={created_at => {
                                if (created_at) {
                                  updateQuery({ created_from: created_at[0], created_to: created_at[1], page: 1 })
                                } else {
                                  updateQuery({ created_from: undefined, created_to: undefined, page: 1 })
                                }
                              }}
                            />
                          </Grid>
                          <Grid size={6}>
                            <UserStatusSelection
                              isTransaction
                              excludeGuestStatuses
                              value={searchQuery.get('status') || ''}
                              onSelect={status => {
                                updateQuery({ status })
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      <Divider />
                    </>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <TableLimit />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SearchInput
                        placeholder={__('Search Employer')}
                        initialValue={searchQuery.get('user_keyword') || ''}
                        onSearch={value => {
                          const current = searchQuery.get('user_keyword') || ''

                          if (value !== current) {
                            updateQuery({ user_keyword: value, page: 1 })
                          }
                        }}
                      />

                      <FilterButton filter={filter} onFilter={() => setFilter(!filter)} />

                      <Button
                        variant='tonal'
                        sx={{
                          borderColor: '#3551F2',
                          borderRadius: '999px',
                          color: '#fff',
                          background: '#3551F2',
                          textTransform: 'capitalize',
                          '&:hover': {
                            borderColor: '#fff',
                            backgroundColor: '#3551F2'
                          }
                        }}
                        startIcon={<i className={'tabler-cash'} />}
                        onClick={() =>
                          navigate(`/cms/finance/transaction/${companyId}/eligibility-rate`, {
                            state: { eligibility_rate: transactionData?.data?.eligibility_rate }
                          })
                        }
                      >
                        {__('setting Eligibility rate default')}
                      </Button>
                    </Box>
                  </Box>
                </Box>

                {/* <Divider /> */}
                <TransactionDetailTableEmployer
                  companyId={companyId}
                  data={employeesData?.data || []}
                  meta={employeesData?.meta as any}
                  refetch={employeesRefetch}
                  onSuccess={employeesRefetch}
                />
              </>
            )}
          </CardContent>
        </Card>
      </BoxPage>

      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Transaction Employer Detail')}
        object_type={ActivityLogTypeEnum.Transaction}
        objectId={companyId || ''}
      />
    </>
  )
}

export default TransactionDetail
